import React, { Ref, useState } from 'react';
import { find, concat, difference, uniq, isEmpty, intersection } from 'lodash';
import styled from 'styled-components';

import Button from '../../../Button';
import Checkbox from '../../../Checkbox';
import Select from '../../../Select';
import OnClickOutside from '../../../OnClickOutside';

const { StyledSelected } = Select;

const SEGMENTS_PLACEHOLDER = 'Выбрать сегмент';

type ReasonType = {
  title: string;
  value: string;
  groupId: string;
};

type SegmentType = {
  title: string;
  value: string;
};

type FormDataType = {
  reasons: string[];
  isUpdating: boolean;
  segment?: string;
  description?: string;
};

interface ICallReasons {
  className?: string;
  forwardedRef: Ref<HTMLFormElement>;
  groups: Array<{ title: string; value: string }>;
  reasons: ReasonType[];
  segments?: SegmentType[];
  requireNoteIds?: string[];
  selectedGroupId?: string | null;
  selectedReasonIds?: string[] | null;
  selectedSegmentId?: string | null;
  description?: string;
  onClickOutside: () => void;
  onSubmit: (data: FormDataType) => void;
}
const CallReasons = ({
  className = '',
  forwardedRef,
  groups,
  reasons,
  segments,
  requireNoteIds = [],
  selectedGroupId = null,
  selectedReasonIds = null,
  selectedSegmentId = null,
  description,
  onClickOutside,
  onSubmit,
}: ICallReasons) => {
  const firstReason =
    find(reasons, { value: selectedReasonIds?.[0] }) || reasons[0];

  const [groupState, setGroupState] = useState(
    selectedGroupId || firstReason.groupId
  );
  const [reasonsState, setReasonsState] = useState(selectedReasonIds);
  const [segmentState, setSegmentState] = useState(selectedSegmentId);
  const [descriptionState, setDescriptionState] = useState(description);
  const [isClosed, setIsClosed] = useState(false);

  const handleClose = () => {
    if (isClosed) {
      return;
    }

    setIsClosed(true);
    onClickOutside();
  };

  const handleBlur = ({ currentTarget, relatedTarget }) => {
    if (!relatedTarget) {
      return;
    }
    if (!currentTarget.contains(relatedTarget)) {
      handleClose();
    }
  };

  const handleGroupChange = ({ detail }) => {
    setGroupState(detail.value);
  };

  const handleReasonChange = ({ target }) => {
    const { dataset, checked } = target;
    const { value } = dataset;

    const newReasons = checked
      ? concat(reasonsState, value)
      : difference(reasonsState, [value]);

    setReasonsState(uniq(newReasons));
  };

  const handleSegmentChange = ({ detail }) => {
    setSegmentState(detail.value);
  };

  const handleDescriptionChange = ({ target }) => {
    setDescriptionState(target.value);
  };

  const handleSubmit = () => {
    const needDescription = !isEmpty(
      intersection(requireNoteIds, reasonsState)
    );

    let formData = {
      reasons: reasonsState || [],
      isUpdating: !isEmpty(selectedReasonIds),
      segment: segmentState ? segmentState : undefined,
      description: needDescription ? descriptionState : undefined,
    };

    onSubmit(formData);
  };

  const segmentsRenderer = () => (
    <>
      <StyledSelect
        className="segments"
        value={segmentState}
        options={segments}
        placeholder={SEGMENTS_PLACEHOLDER}
        onChange={handleSegmentChange}
      />
      <StyledSplit />
    </>
  );

  const reasonsRenderer = () =>
    reasons.map(
      ({ title, value, groupId }) =>
        groupId === groupState && (
          <StyledCheckbox
            key={value}
            className="reasons"
            data-value={value}
            checked={reasonsState?.includes(value)}
            onChange={handleReasonChange}
          >
            {title}
          </StyledCheckbox>
        )
    );

  const segmentFilled =
    isEmpty(segments) || (!isEmpty(segments) && segmentState);
  const needDescription = !isEmpty(intersection(requireNoteIds, reasonsState));
  const descriptionFilled =
    !needDescription || (needDescription && !!descriptionState);
  const isFilled = segmentFilled && !isEmpty(reasonsState) && descriptionFilled;

  return (
    <OnClickOutside onBlur={handleBlur} onClickOutside={handleClose}>
      <StyledCallReasons ref={forwardedRef} className={className}>
        {segments && segmentsRenderer()}
        <StyledTitle>Причина обращения</StyledTitle>
        <StyledSelect
          options={groups}
          value={groupState}
          onChange={handleGroupChange}
        />
        <StyledReasonList>{reasonsRenderer()}</StyledReasonList>
        {needDescription && (
          <StyledDescription
            value={descriptionState}
            placeholder="Уточните причину"
            onChange={handleDescriptionChange}
          />
        )}
        <StyledButton disabled={!isFilled} onClick={handleSubmit}>
          Готово
        </StyledButton>
      </StyledCallReasons>
    </OnClickOutside>
  );
};

const StyledCallReasons = styled.form`
  font-size: 14px;
`;

const StyledTitle = styled.b`
  display: block;
  margin: 0 10px 10px;
  width: calc(100% - 20px);
`;

const StyledSelect = styled(Select)`
  margin: 0 10px 10px;
  height: 41px;
  width: calc(100% - 20px);

  ${StyledSelected} {
    border-radius: 4px;
    border: solid 1px #17181a;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 10px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledSplit = styled.hr`
  border: none;
  background: #dedede;
  height: 1px;
  width: 100%;
  margin-bottom: 5px;
`;

const StyledReasonList = styled.div`
  margin-bottom: 10px;
  max-height: 150px;
  overflow-y: auto;
  padding: 5px 10px;
  width: 100%;
`;

const StyledDescription = styled.textarea`
  border-radius: 4px;
  border: solid 1px #c2c5cc;
  box-sizing: border-box;
  font-size: 14px;
  height: 53px;
  margin: 0 10px 10px;
  padding: 6px 12px;
  resize: none;
  width: calc(100% - 20px);
`;

const StyledButton = styled(Button)`
  background-color: #007aff;
  border: none;
  color: white;
  font-size: 13px;
  margin: 0 10px;
  width: calc(100% - 20px);

  &:hover {
    color: white;
  }

  ${props =>
    props.disabled &&
    `
    opacity: .3
  `}
`;

export default React.forwardRef<
  HTMLFormElement,
  Omit<ICallReasons, 'forwardedRef'>
>((props, ref) => <CallReasons {...props} forwardedRef={ref} />);
